import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { Feature, Polygon as GeoJsonPolygon } from "geojson";
import xss from "xss";
import { format } from "date-fns";
import { id as ID } from "date-fns/locale";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatToMask(format: string): string {
  return format.replace(/[a-zA-Z]/g, "_");
}

export function formatDate(
  date?: string | Date | null,
  option?: { time: boolean }
) {
  if (!date) return "-";
  return format(date, `EEEE, d MMMM yyyy ${option?.time ? "HH:mm" : ""}`, {
    locale: ID,
  });
}

export const formatFileName = (name: string) => {
  if (!name) return "Tidak ada nama file";
  const tempName = name?.split("/");
  const newName = tempName[tempName.length - 1];
  return newName;
};

export function formatErrorMessages(
  errorData: Record<string, string[] | string>
): string {
  return Object.entries(errorData)
    .map(([field, messages]) => {
      const readableField = field
        .replace(/([A-Z])/g, " $1") // camelCase to space
        .replace(/^./, (str) => str.toUpperCase()); // Capitalize first letter

      const text =
        Array.isArray(messages) && messages.length > 0
          ? messages.map((msg) => `• ${readableField}: ${msg}`).join("\n")
          : `• ${readableField}: ${messages}`;

      return text;
    })
    .join("\n");
}

/** Helper: base64URL → Uint8Array (untuk VAPID public key) */
export function urlBase64ToUint8Array(base64: string) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const base64Safe = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64Safe);
  return Uint8Array.from([...raw].map((ch) => ch.charCodeAt(0)));
}

export function arrayBufferToBase64(buf: ArrayBuffer | null) {
  if (!buf) return "";
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}

export function cleanHTML(input: string): string {
  return xss(input, {
    whiteList: {},
    stripIgnoreTag: true, // hapus tag yang ga di whitelist
    stripIgnoreTagBody: ["script", "style"], // buang isi script/style juga
  });
}

export const getCoords = (feature: Feature<GeoJsonPolygon, AreaProperties>) => {
  if (feature.geometry.type === "Polygon") {
    return feature.geometry.coordinates[0].map(([lng, lat]) => ({ lat, lng }));
  }
  if (feature.geometry.type === "MultiPolygon") {
    return feature.geometry.coordinates[0][0].map(([lng, lat]: any) => ({
      lat,
      lng,
    }));
  }
  return [];
};

export function getPolygonCenter(coords: google.maps.LatLngLiteral[]) {
  let minLat = coords[0].lat,
    maxLat = coords[0].lat,
    minLng = coords[0].lng,
    maxLng = coords[0].lng;

  coords.forEach(({ lat, lng }) => {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  });

  return {
    lat: (minLat + maxLat) / 2,
    lng: (minLng + maxLng) / 2,
  };
}

export function formatDateDay(dateString: string | null | undefined): string {
  if (!dateString) return "-";
  try {
    return format(new Date(dateString), "dd MMMM yyyy", { locale: ID });
  } catch {
    return "-";
  }
}

export function SubsCategory(category?: string) {
  switch (category) {
    case "SUBSIDI":
      return "Subsidi";
    case "NONSUBSIDI":
      return "Non Subsidi";
    default:
      return "-";
  }
}

export function UserJabatan(category?: string) {
  switch (category) {
    case "ANGGOTA":
      return "Anggota";
    case "KETUA":
      return "Ketua";
    default:
      return "-";
  }
}

export function UserGender(category?: string) {
  switch (category) {
    case "MALE":
      return "Laki-Laki";
    case "FEMALE":
      return "Perempuan";
    default:
      return "-";
  }
}

export function StatusSubs(category?: string) {
  switch (category) {
    case "DIAJUKAN":
      return "Diajukan";
    case "DIAJUKAN_INSTANSI":
      return "Diajukan Instansi";
    case "SETUJUI_ADMIN_KECAMATAN":
      return "Disetujui Admin Kecamatan";
    case "TOLAK_ADMIN_KECAMATAN":
      return "Ditolak Admin Kecamatan";
    case "SETUJUI_PENYULUH":
      return "Disetujui Penyuluh";
    case "TOLAK_PENYULUH":
      return "Ditolak Penyuluh";
    case "SETUJUI_DINAS": // <- kalau ini typo, harusnya "SETUJUI_DINAS"
      return "Disetujui Dinas";
    case "TOLAK_DINAS":
      return "Ditolak Dinas";
    case "TERKIRIM":
      return "Terkirim";
    case "SELESAI":
      return "Selesai";
    case "TERLAMBAT":
      return "Terlambat";
    default:
      return "-";
  }
}
